import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { apiService } from '../../services/apiService';
import type { ChangesetDiff, DraftReview } from '../../types/keywords';
import { ConfirmDialog } from './ConfirmDialog';
import type { KeywordVocabulary } from './useKeywordVocabulary';
import { describeError, formatShare } from './utils';

const SECTIONS: (keyof Omit<ChangesetDiff, 'merged'>)[] = ['added', 'removed', 'relabeled', 'reevaluated'];

function isEmpty(diff: ChangesetDiff): boolean {
  return Object.values(diff).every((entries) => entries.length === 0);
}

interface ReviewTabProps {
  vocabulary: KeywordVocabulary;
  onCommitted: () => void;
}

export function ReviewTab({ vocabulary, onCommitted }: ReviewTabProps) {
  const { t, i18n } = useTranslation('admin');
  const { draft, active, etag, reload } = vocabulary;
  const [review, setReview] = useState<DraftReview | null>(null);
  const [fullRetag, setFullRetag] = useState(false);
  const [warningsChecked, setWarningsChecked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Review and draft are reloaded together, so the commit refers to the draft shown here.
  useEffect(() => {
    Promise.all([apiService.reviewKeywordDraft(), reload()]).then(
      ([response]) => setReview(response),
      (err) => setError(describeError(err, t)),
    );
  }, [reload, t]);

  const labels = useMemo(() => {
    const byId = new Map<string, string>();
    for (const k of [...(active?.keywords ?? []), ...(draft?.keywords ?? [])]) byId.set(k.id, k.labels.en);
    return byId;
  }, [active, draft]);

  if (error && !review) return <Alert severity="error">{error}</Alert>;
  if (!review) return <CircularProgress />;

  const retag = fullRetag || review.full_retag_required;
  const nothingToCommit = isEmpty(review.diff) && !retag;
  const llmCalls = retag ? review.effort.full_retag_llm_calls : review.effort.incremental_llm_calls;
  const label = (id: string) => labels.get(id) ?? id;
  // Warnings never block a commit, but the curator has to confirm having looked at them.
  const warningCount =
    review.warnings.length + review.similarity_warnings.length + review.mention_warnings.length;

  const commit = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiService.commitKeywordDraft(etag, retag);
      setConfirming(false);
      onCommitted();
    } catch (err) {
      setConfirming(false);
      setError(describeError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {review.errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography fontWeight={500}>{t('review.errors')}</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {review.errors.map((e) => <li key={e}>{e}</li>)}
          </Box>
        </Alert>
      )}
      {review.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography fontWeight={500}>{t('review.warnings')}</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {review.warnings.map((w) => (
              <li key={`${w.keyword_id}-${w.text}-${w.reason}`}>
                {label(w.keyword_id)}: "{w.text}" – {t(`review.overlap.${w.reason}`)}
              </li>
            ))}
          </Box>
        </Alert>
      )}
      {review.similarity_warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography fontWeight={500}>{t('review.similarWarnings')}</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {review.similarity_warnings.map((w) => (
              <li key={`${w.keyword_id}-${w.similar_to}`}>
                {label(w.keyword_id)} ↔ {label(w.similar_to)} ({w.similarity.toFixed(2)})
              </li>
            ))}
          </Box>
        </Alert>
      )}
      {review.mention_warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography fontWeight={500}>{t('review.mentionWarnings')}</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {review.mention_warnings.map((m) => (
              <li key={m.keyword_id}>
                {label(m.keyword_id)}:{' '}
                {t(m.too_rare ? 'review.mentionsRare' : 'review.mentionsBroad', {
                  count: m.documents,
                  share: formatShare(m.share),
                })}
              </li>
            ))}
          </Box>
        </Alert>
      )}
      {!review.similarity_checked && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('review.similarityUnavailable')}</Alert>
      )}

      {isEmpty(review.diff) ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>{t('review.noChanges')}</Typography>
      ) : (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {SECTIONS.filter((s) => review.diff[s].length > 0).map((section) => (
            <Box key={section}>
              <Typography variant="subtitle2">{t(`review.sections.${section}`)}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {review.diff[section].map((id) => <Chip key={id} size="small" label={label(id)} />)}
              </Stack>
            </Box>
          ))}
          {review.diff.merged.length > 0 && (
            <Box>
              <Typography variant="subtitle2">{t('review.sections.merged')}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {review.diff.merged.map((m) => (
                  <Chip key={m.from_id} size="small" label={`${label(m.from_id)} → ${label(m.to_id)}`} />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={retag}
            disabled={review.full_retag_required}
            onChange={(e) => setFullRetag(e.target.checked)}
          />
        }
        label={review.full_retag_required ? t('review.firstCommit') : t('review.fullRetag')}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('review.effort', { count: llmCalls, formatted: llmCalls.toLocaleString(i18n.language) })}
      </Typography>

      {warningCount > 0 && (
        <FormControlLabel
          sx={{ display: 'flex', mb: 1 }}
          control={<Checkbox checked={warningsChecked} onChange={(e) => setWarningsChecked(e.target.checked)} />}
          label={t('review.warningsChecked', { count: warningCount })}
        />
      )}

      <Button
        variant="contained"
        disabled={review.errors.length > 0 || nothingToCommit || (warningCount > 0 && !warningsChecked)}
        onClick={() => setConfirming(true)}
      >
        {t('review.commit')}
      </Button>

      <ConfirmDialog
        open={confirming}
        title={t('review.confirmTitle')}
        confirmLabel={t('review.commit')}
        busy={busy}
        onConfirm={commit}
        onClose={() => setConfirming(false)}
      >
        {t('review.confirmText', { formatted: llmCalls.toLocaleString(i18n.language) })}
      </ConfirmDialog>
    </Box>
  );
}
